import { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';

import { AUTH_TOKEN_NAME, getAuthKey } from '../api/auth-utils';
import { runConcurrentRequests } from './run-concurrent-requests';

const MAX_CACHE_ENTRIES = 50;
const DEFAULT_CACHE_TIME_MS = 5 * 60 * 1000; // 5 minute

// Cache utilities
const requestCache = new Map();

const generateCacheKey = (method, url, params, payload, authKey) => {
    return `${authKey}-${method}-${url}-${JSON.stringify(params)}-${JSON.stringify(payload)}`;
};

const isCacheValid = (cachedItem, cacheTime) => {
    return Date.now() - cachedItem.timestamp < cacheTime;
};

const clearExpiredCache = () => {
    const now = Date.now();
    for (const [key, item] of requestCache.entries()) {
        if (now - item.timestamp > item.cacheTime) {
            requestCache.delete(key);
        }
    }
};

// Response processing helper
const processResponse = (responseData, requestConfig) => {
    if (!requestConfig || !responseData) return responseData;

    return (responseData.content && (requestConfig.fullResp ? responseData : responseData.content)) || responseData;
};

/**
 * Universal React hook for making HTTP requests with support for single and multiple parallel requests
 *
 * @param {Object|Array} config - Configuration object or array of configuration objects for multiple requests
 * @param {string} config.method - HTTP method (GET, POST, PUT, DELETE, etc.)
 * @param {string} config.url - Request URL
 * @param {Object} [config.params] - Query parameters
 * @param {*} [config.payload] - Request body data
 * @param {Function} [config.responseHandler] - Callback function to handle successful response
 * @param {boolean} [config.initialCondition] - Condition to control when the request should be executed
 * @param {Function} [config.processingSetter] - Callback to set processing state externally
 * @param {boolean} [config.repeat] - Flag to trigger request repetition
 * @param {Function} [config.onRepeat] - Callback executed when request is repeated
 * @param {Function} [config.onRequestError] - Callback executed when request fails
 * @param {boolean} [config.fullResp] - Whether to return full response or just content
 * @param {boolean} [config.isDisabled] - Disable the request
 * @param {boolean} [config.enableCache] - Enable response caching
 * @param {number} [config.cacheTime] - Cache lifetime in milliseconds (default: 5 minutes)
 * @param {number} [config.cacheCleanupProbability=0.1] - Expired cache cleanup probability (0..1).
 *   The largest probability in a batch is used; TTL checks and the entry limit always apply.
 * @param {boolean} [config.refetchOnChange=true] - Request again when the url, params or payload change; the batch in
 *   flight is cancelled. false: a changed request is sent only after initialCondition or isDisabled changes, or by
 *   handleRepeat (e.g. a POST that must be sent at a moment the caller chooses)
 *
 * @param {Object} [options] - Options for the whole batch
 * @param {number} [options.maxConcurrent=Infinity] - Concurrent HTTP requests; finite values are floored and
 *   clamped to at least one. Cached responses do not occupy slots. Changing the limit restarts the batch.
 * @param {string} [options.contextKey] - Changing this key cancels/restarts the batch even with the same URLs.
 *
 * @param {boolean} [options.notifyOnComplete=false] - Notify handlers after each response; data/error still wait for all.
 *
 * @returns {Object} Request state and control methods
 * @returns {boolean} return.processing - True if request is in progress or initializing
 * @returns {*|Array} return.data - Response data (single object for single request, array for multiple requests)
 * @returns {boolean|Array} return.error - Error state (single boolean for single request, array for multiple requests)
 * @returns {Function} return.handleCancel - Function to cancel the current request
 * @returns {Function} return.handleRepeat - Function to repeat the request
 *
 * @example
 * // Single request
 * const { data, processing, error } = useUniversalFetchData({
 *   method: 'GET',
 *   url: '/api/users',
 *   responseHandler: (data) => console.log('Users loaded:', data)
 * });
 *
 * @example
 * // Multiple parallel requests
 * const { data, processing, error } = useUniversalFetchData([
 *   {
 *     method: 'GET',
 *     url: '/api/users',
 *     enableCache: true
 *   },
 *   {
 *     method: 'GET',
 *     url: '/api/posts',
 *     responseHandler: (posts) => setPosts(posts)
 *   }
 * ]);
 * // data = [usersData, postsData]
 * // error = [usersError, postsError]
 *
 * @example
 * // Controlled request with caching
 * const { data, handleRepeat } = useUniversalFetchData({
 *   method: 'POST',
 *   url: '/api/users',
 *   payload: userData,
 *   initialCondition: shouldSubmit, // Only execute when this is true
 *   enableCache: false,
 *   onRequestError: () => showError('Request failed')
 * });
 */
// eslint-disable-next-line tpe/max-component-size -- TODO: legacy large component (baseline)
export const useUniversalFetchData = (
    config,
    { maxConcurrent = Infinity, contextKey, notifyOnComplete = false } = {}
) => {
    const concurrency = Number.isFinite(maxConcurrent) ? Math.max(1, Math.floor(maxConcurrent)) : Infinity;
    const authKey = getAuthKey();
    const isMultiple = Array.isArray(config);
    const requests = isMultiple ? config : [config];

    // Basic request validation and normalization
    const validatedRequests = requests.map(req => ({
        ...req,
        url: req?.url ? String(req.url) : '',
        method: (req?.method || 'GET').toUpperCase(),
        cacheTime: req?.cacheTime || DEFAULT_CACHE_TIME_MS
    }));

    const [data, setData] = useState(isMultiple ? [] : null);
    const [isInit, setInit] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState(isMultiple ? [] : false);

    const handleSetData = useCallback(
        el => {
            if (!el) {
                return;
            }

            if (Array.isArray(el) && el.filter(item => item != null).length === 0) {
                return;
            }

            // Failed requests stay as null slots: compacting the array shifted later results,
            // so data[i] no longer matched request i
            setData(isMultiple && Array.isArray(el) ? Array.from(el, item => item ?? null) : el);
        },
        [isMultiple]
    );

    const cancelSourceRef = useRef(null);
    const mountedRef = useRef(true);

    const cancelCurrentBatch = useCallback(() => {
        if (!cancelSourceRef.current) {
            return;
        }

        cancelSourceRef.current.cancel();
        cancelSourceRef.current = null;
    }, []);

    // validatedRequests is rebuilt on every render; reading it through a ref keeps handleRepeat stable,
    // otherwise the repeat effect below re-fires on every render while `repeat` stays true
    const requestsRef = useRef(validatedRequests);
    requestsRef.current = validatedRequests;

    // Repeat handler
    const handleRepeat = useCallback(() => {
        cancelCurrentBatch();

        const currentRequests = requestsRef.current;
        const anyDisabled = currentRequests.some(req => req.isDisabled);
        if (anyDisabled) {
            return;
        }

        setProcessing(false);
        setInit(true);
        handleSetData(isMultiple ? [] : null);
        setError(isMultiple ? [] : false);

        for (const req of currentRequests) {
            req.onRepeat?.();
        }
    }, [cancelCurrentBatch, handleSetData, isMultiple]);

    // Cancel handler
    const handleCancel = useCallback(() => {
        cancelCurrentBatch();
        setInit(false);
        setProcessing(false);
    }, [cancelCurrentBatch]);

    // Effects below are keyed by these strings instead of the request objects, which are new on every
    // render; the effects read the current requests through requestsRef
    const repeatKey = validatedRequests.map(req => req.repeat).join('|');
    const initKey = validatedRequests.map(req => `${req.initialCondition}-${req.isDisabled}`).join('|');
    const optionsKey = JSON.stringify([concurrency, contextKey, notifyOnComplete]);
    const requestKey = `${authKey}|${optionsKey}|${validatedRequests
        .map(
            req =>
                `${req.method}-${req.url}-${JSON.stringify(req.params)}-${JSON.stringify(req.payload)}-${
                    req.isDisabled
                }`
        )
        .join('|')}`;
    // A changed request initializes again, as a changed initialCondition does (unless refetchOnChange is false).
    // Before, only the number of the requests did: a list of the same length with other ids was not requested.
    const refetchKey = `${optionsKey}|${
        validatedRequests.some(req => req.refetchOnChange !== false) ? requestKey : ''
    }`;

    // Effect for repeat triggers
    useEffect(() => {
        const currentRequests = requestsRef.current;
        const anyDisabled = currentRequests.some(req => req.isDisabled);
        const anyRepeat = currentRequests.some(req => req.repeat);

        if (!anyRepeat || anyDisabled) {
            return;
        }

        handleRepeat();
    }, [repeatKey, handleRepeat]);

    // Initialization effect
    useEffect(() => {
        const currentRequests = requestsRef.current;
        const anyDisabled = currentRequests.some(req => req.isDisabled);
        const shouldInitialize = currentRequests.every(req => {
            if (req.isDisabled) return false;
            if (req.initialCondition === false) return false;

            return req.initialCondition === undefined || !!req.initialCondition;
        });

        if (anyDisabled || !shouldInitialize) {
            cancelCurrentBatch();
            setInit(false);
            setProcessing(false);

            return;
        }

        setInit(true);
        handleSetData(isMultiple ? [] : null);

        return cancelCurrentBatch;
    }, [initKey, refetchKey, isMultiple, handleSetData, cancelCurrentBatch]);

    // Processing state effect. Depends on `processing` only: validatedRequests is a new array on every
    // render, so with it in deps every setter was called on every render.
    useEffect(() => {
        if (typeof processing !== 'boolean') {
            return;
        }

        for (const req of requestsRef.current) {
            if (typeof req.processingSetter === 'function') {
                req.processingSetter(processing);
            }
        }
    }, [processing]);

    // Main request effect
    useEffect(() => {
        // Requests of this batch; result indices and handlers refer to this snapshot
        const batchRequests = requestsRef.current;

        if (!isInit || batchRequests.some(req => req.isDisabled)) {
            return;
        }

        // Basic URL validation
        const invalidRequest = batchRequests.find(
            req => !req.url || req.url.includes('null') || req.url.includes('undefined')
        );

        if (invalidRequest) {
            // eslint-disable-next-line no-console
            console.error('Invalid URL:', invalidRequest.url);
            setInit(false);
            setError(
                isMultiple ? batchRequests.map((_, index) => index === batchRequests.indexOf(invalidRequest)) : true
            );

            return;
        }

        setError(isMultiple ? [] : false);

        // Keys and headers use this render's session, including for late responses.
        const cleanupProbability = Math.max(0, ...batchRequests.map(req => req.cacheCleanupProbability ?? 0.1));
        if (Math.random() < cleanupProbability) {
            clearExpiredCache();
        }

        // Check cache for all requests. Cached entries already hold processed responses.
        const cachedResults = batchRequests.map(req => {
            if (!req.enableCache) {
                return undefined;
            }

            const cachedItem = requestCache.get(
                generateCacheKey(req.method, req.url, req.params, req.payload, authKey)
            );

            return cachedItem && isCacheValid(cachedItem, req.cacheTime) ? cachedItem.data : undefined;
        });
        const cachedIndices = batchRequests
            .map((_, index) => index)
            .filter(index => cachedResults[index] !== undefined);
        const uncachedIndices = batchRequests
            .map((_, index) => index)
            .filter(index => cachedResults[index] === undefined);

        // Cached requests get their handlers called like fresh ones (previously skipped in a mixed batch)
        const notifyCached = () => {
            for (const index of cachedIndices) {
                batchRequests[index].responseHandler?.(cachedResults[index]);
            }
        };

        if (notifyOnComplete) notifyCached();

        // Return early if all requests are cached
        if (uncachedIndices.length === 0) {
            cancelCurrentBatch();
            if (!notifyOnComplete) notifyCached();

            setProcessing(false);
            setInit(false);

            handleSetData(isMultiple ? cachedResults : cachedResults[0]);

            return;
        }

        setInit(false);
        setProcessing(true);

        // A newer batch replaces the one in flight
        cancelCurrentBatch();
        const cancelSource = axios.CancelToken.source();
        cancelSourceRef.current = cancelSource;

        // Create each HTTP request only when a worker slot is available.
        const sendRequest = originalIndex => {
            const req = batchRequests[originalIndex];

            const requestConfig = {
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                    [AUTH_TOKEN_NAME]: authKey
                },
                method: req.method,
                url: req.url,
                params: req.params,
                data: req.payload,
                cancelToken: cancelSource.token,
                timeout: 30000
            };

            return axios(requestConfig)
                .then(response => {
                    if (!response?.data) {
                        // eslint-disable-next-line no-console
                        console.warn('Empty response for:', req.url);

                        return { index: originalIndex, data: null, error: true };
                    }

                    const resp = processResponse(response.data, req);

                    // Cache successful response
                    if (req.enableCache && mountedRef.current && cancelSourceRef.current === cancelSource) {
                        const cacheKey = generateCacheKey(req.method, req.url, req.params, req.payload, authKey);
                        requestCache.delete(cacheKey);
                        if (requestCache.size >= MAX_CACHE_ENTRIES) {
                            requestCache.delete(requestCache.keys().next().value);
                        }
                        requestCache.set(cacheKey, {
                            data: resp,
                            timestamp: Date.now(),
                            cacheTime: req.cacheTime
                        });
                    }

                    return { index: originalIndex, data: resp, error: false };
                })
                .catch(err => {
                    if (!axios.isCancel(err)) {
                        // eslint-disable-next-line no-console
                        console.error('Request failed:', req.url, err.message);
                    }

                    return { index: originalIndex, data: null, error: true };
                })
                .then(result => {
                    if (notifyOnComplete && mountedRef.current && cancelSourceRef.current === cancelSource) {
                        if (result.error) req.onRequestError?.();
                        else req.responseHandler?.(result.data);
                    }

                    return result;
                });
        };

        runConcurrentRequests({
            items: uncachedIndices,
            limit: concurrency,
            request: sendRequest,
            isCancelled: () => !mountedRef.current || cancelSourceRef.current !== cancelSource
        }).then(results => {
            // Skip if unmounted, cancelled or replaced by a newer batch
            if (!mountedRef.current || cancelSourceRef.current !== cancelSource) {
                return;
            }

            cancelSourceRef.current = null;

            // Combine cached and fresh results; cached requests count as successful
            const resultByIndex = new Map(results.map(result => [result.index, result]));
            const allResults = cachedResults.map((cached, index) =>
                resultByIndex.has(index) ? resultByIndex.get(index).data ?? undefined : cached
            );
            const errors = batchRequests.map((_, index) => !!resultByIndex.get(index)?.error);

            if (!notifyOnComplete) notifyCached();
            for (const result of results) {
                if (!notifyOnComplete && !result.error && result.data) {
                    batchRequests[result.index].responseHandler?.(result.data);
                }
            }

            setProcessing(false);

            // Set even when every request failed: the data of the previous batch must not stay as if it were this one's
            setData(isMultiple ? Array.from(allResults, item => item ?? null) : allResults[0] ?? null);
            setError(isMultiple ? errors : errors[0]);

            // Call error handlers for failed requests
            for (const [index, isError] of errors.entries()) {
                if (!notifyOnComplete && isError) {
                    batchRequests[index].onRequestError?.();
                }
            }
        });
    }, [isInit, requestKey, isMultiple, cancelCurrentBatch, handleSetData, authKey, concurrency, notifyOnComplete]);

    // Cleanup effect
    useEffect(() => {
        mountedRef.current = true;

        return () => {
            mountedRef.current = false;
            cancelCurrentBatch();
        };
    }, [cancelCurrentBatch]);

    return {
        processing: isInit || processing,
        data,
        error,
        handleCancel,
        handleRepeat
    };
};
